/*
 * <BeBarberPole> -- React wrapper for the .be-barber-pole CSS component.
 *
 * The BeOS indeterminate-progress indicator: an animated blue/white diagonal
 * "barber pole" with the convex 3D "tube" shading of LibWalter's BarberPole
 * FANCY style (see components/barber-pole.css for provenance). BeOS has no
 * indeterminate BStatusBar, so this is the faithful stand-in.
 *
 * As with the other components this file does not import the CSS; the consumer
 * loads the bundle (or src) separately.
 *
 *   <BeBarberPole aria-label="Searching…" />
 *   <BeBarberPole state="paused" />
 *   <BeBarberPole state="idle" />
 *
 * Optional label/trailing text sits above the bar on the sides, exactly like
 * <BeProgressBar> (they share the .be-progress-text row), so a labelled barber
 * pole is a drop-in for the determinate bar:
 *
 *   <BeBarberPole label="Searching…" trailing="Please wait" />
 */

import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeBarberPoleState = "progressing" | "paused" | "idle";

export type BeBarberPoleProps = BeProps<
	"div",
	{
		/** Animation state: progressing (default), paused (frozen), idle (blank well). */
		state?: BeBarberPoleState;

		/**
		 * Optional left-aligned label shown on top of the bar (e.g. "Searching…").
		 * Indeterminate progress has no percentage, so text only sits on the sides.
		 */
		label?: string;
		/** Optional right-aligned trailing text shown on top of the bar. */
		trailing?: string;

		/** Release the preferred width and span the parent's inline size. */
		fluid?: boolean;
	}
>;

/** The ref lands on the BAR, which is the element with a size, even when a
 * label wrapper is rendered around it. */
export const BeBarberPole = beComponent<BeBarberPoleProps, HTMLDivElement>("BeBarberPole", (props, be) => {
	const { state = "progressing", scale, label, trailing, fluid, className, style, ...rest } = props;

	const hasText = label != null || trailing != null;

	// with a wrapper the width belongs to the wrapper, not the bar
	const root = be("be-barber-pole", state === "paused" && "is-paused", state === "idle" && "is-idle", fluid && !hasText && "is-fluid");

	// An indeterminate progressbar omits aria-valuenow.
	const bar = (
		<div
			role="progressbar"
			aria-label={props["aria-label"] ?? "Working"}
			{...rest}
			{...root}
			// when wrapped, --be-scale lives on the field so the text scales too
			style={hasText ? style : root.style}
		>
			<div className="be-barber-pole__stripes" />
		</div>
	);

	if (!hasText) return bar;

	const scaleStyle: React.CSSProperties | undefined = scale !== undefined ? { ...style, "--be-scale": scale } : style;

	return (
		<div className={fluid ? "be-barber-pole-field is-fluid" : "be-barber-pole-field"} style={scaleStyle}>
			<div className="be-progress-text">
				{pixelText(label, "be-progress-text__label")}
				<span className="be-progress-text__trailing">{pixelText(trailing)}</span>
			</div>
			{bar}
		</div>
	);
});
